import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { 
  PlusIcon, 
  TrashIcon, 
  PencilIcon,
  CpuChipIcon,
  TruckIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

type TabType = 'vehicles' | 'fees' | 'zones' | 'time-rules' | 'ai-config';

interface VehiclePricing {
  id: number;
  vehicle_type: string;
  flat_fee: number;
  rate_per_km: number;
  rate_per_minute: number;
  minimum_fare: number;
  maximum_fare: number;
  max_capacity_kg: number;
  apply_weight_surcharge: boolean;
  apply_zone_multipliers: boolean;
  apply_time_pricing: boolean;
  is_active: boolean;
}

interface PlatformFee {
  id: number;
  fee_name: string;
  fee_type: string;
  fee_value: number;
  fee_cap: number | null;
  is_active: boolean;
}

interface PricingZone {
  id: number;
  zone_name: string;
  zone_type: string;
  multiplier: number;
  areas: string[];
  is_active: boolean;
}

interface TimePricingRule {
  id: number;
  rule_name: string;
  rule_type: string;
  multiplier: number;
  day_of_week: number[];
  start_hour: number;
  end_hour: number;
  is_active: boolean;
}

interface AIPricingConfig {
  id?: number;
  is_enabled: boolean;
  max_adjustment_percent: number;
  min_adjustment_percent: number;
  consider_time_of_day: boolean;
  consider_demand: boolean;
  consider_location_zone: boolean;
  consider_cost_of_living: boolean;
}

const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const PricingManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('vehicles');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [vehiclePricing, setVehiclePricing] = useState<VehiclePricing[]>([]);
  const [platformFees, setPlatformFees] = useState<PlatformFee[]>([]);
  const [pricingZones, setPricingZones] = useState<PricingZone[]>([]);
  const [timePricingRules, setTimePricingRules] = useState<TimePricingRule[]>([]);
  const [aiConfig, setAIConfig] = useState<AIPricingConfig>({
    is_enabled: false,
    max_adjustment_percent: 50,
    min_adjustment_percent: -30,
    consider_time_of_day: true,
    consider_demand: true,
    consider_location_zone: true,
    consider_cost_of_living: true
  });

  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [showTimeRuleModal, setShowTimeRuleModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [vehicles, fees, zones, timeRules, aiConfigData] = await Promise.all([
        adminAPI.getVehiclePricing().catch(() => []),
        adminAPI.getPlatformFees().catch(() => []),
        adminAPI.getPricingZones().catch(() => []),
        adminAPI.getTimePricingRules().catch(() => []),
        adminAPI.getAIPricingConfig().catch(() => null)
      ]);
      
      setVehiclePricing(vehicles || []);
      setPlatformFees(fees || []);
      setPricingZones(zones || []);
      setTimePricingRules(timeRules || []);
      if (aiConfigData) {
        setAIConfig(aiConfigData);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    if (type === 'success') {
      setSuccess(message);
      setError('');
    } else {
      setError(message);
      setSuccess('');
    }
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 4000);
  };

  const handleSaveAIConfig = async () => {
    setSaving(true);
    try {
      await adminAPI.updateAIPricingConfig(aiConfig);
      showNotification('AI pricing configuration saved successfully', 'success');
    } catch (err: any) {
      showNotification(err.message || 'Failed to save AI configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVehicle = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this vehicle pricing?')) return;
    try {
      await adminAPI.deleteVehiclePricing(id);
      setVehiclePricing(prev => prev.filter(v => v.id !== id));
      showNotification('Vehicle pricing deleted', 'success');
    } catch (err: any) {
      showNotification(err.message || 'Failed to delete', 'error');
    }
  };

  const handleDeleteFee = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this fee?')) return;
    try {
      await adminAPI.deletePlatformFee(id);
      setPlatformFees(prev => prev.filter(f => f.id !== id));
      showNotification('Fee deleted', 'success');
    } catch (err: any) {
      showNotification(err.message || 'Failed to delete', 'error');
    }
  };

  const handleDeleteZone = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this zone?')) return;
    try {
      await adminAPI.deletePricingZone(id);
      setPricingZones(prev => prev.filter(z => z.id !== id));
      showNotification('Zone deleted', 'success');
    } catch (err: any) {
      showNotification(err.message || 'Failed to delete', 'error');
    }
  };

  const handleDeleteTimeRule = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this time rule?')) return;
    try {
      await adminAPI.deleteTimePricingRule(id);
      setTimePricingRules(prev => prev.filter(r => r.id !== id));
      showNotification('Time rule deleted', 'success');
    } catch (err: any) {
      showNotification(err.message || 'Failed to delete', 'error');
    }
  };

  const tabs = [
    { id: 'vehicles' as TabType, name: 'Vehicle Types', icon: TruckIcon },
    { id: 'fees' as TabType, name: 'Platform Fees', icon: CurrencyDollarIcon },
    { id: 'zones' as TabType, name: 'Pricing Zones', icon: MapPinIcon },
    { id: 'time-rules' as TabType, name: 'Time Rules', icon: ClockIcon },
    { id: 'ai-config' as TabType, name: 'AI Settings', icon: CpuChipIcon },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg text-gray-600 font-poppins">Loading pricing configurations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 font-poppins">
      <div className="bg-white rounded-volexx-lg shadow-volexx-soft border border-gray-200 p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Advanced Pricing Management</h1>
          <p className="text-gray-600">
            Configure vehicle pricing, platform fees, location zones, time-based rules, and AI-powered adjustments.
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-volexx">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-volexx">
            {success}
          </div>
        )}

        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-1 overflow-x-auto" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium rounded-t-lg flex items-center gap-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-sky-100 text-sky-700 border-b-2 border-sky-500'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'vehicles' && (
          <VehiclePricingTab
            vehicles={vehiclePricing}
            onAdd={() => { setEditingItem(null); setShowVehicleModal(true); }}
            onEdit={(item) => { setEditingItem(item); setShowVehicleModal(true); }}
            onDelete={handleDeleteVehicle}
            onRefresh={fetchAllData}
          />
        )}

        {activeTab === 'fees' && (
          <PlatformFeesTab
            fees={platformFees}
            onAdd={() => { setEditingItem(null); setShowFeeModal(true); }}
            onEdit={(item) => { setEditingItem(item); setShowFeeModal(true); }}
            onDelete={handleDeleteFee}
            onRefresh={fetchAllData}
          />
        )}

        {activeTab === 'zones' && (
          <PricingZonesTab
            zones={pricingZones}
            onAdd={() => { setEditingItem(null); setShowZoneModal(true); }}
            onEdit={(item) => { setEditingItem(item); setShowZoneModal(true); }}
            onDelete={handleDeleteZone}
            onRefresh={fetchAllData}
          />
        )}

        {activeTab === 'time-rules' && (
          <TimePricingTab
            rules={timePricingRules}
            onAdd={() => { setEditingItem(null); setShowTimeRuleModal(true); }}
            onEdit={(item) => { setEditingItem(item); setShowTimeRuleModal(true); }}
            onDelete={handleDeleteTimeRule}
            onRefresh={fetchAllData}
          />
        )}

        {activeTab === 'ai-config' && (
          <AIConfigTab
            config={aiConfig}
            setConfig={setAIConfig}
            onSave={handleSaveAIConfig}
            saving={saving}
          />
        )}
      </div>

      {showVehicleModal && (
        <VehicleModal
          item={editingItem}
          onClose={() => setShowVehicleModal(false)}
          onSuccess={() => { setShowVehicleModal(false); fetchAllData(); showNotification('Vehicle pricing saved', 'success'); }}
        />
      )}

      {showFeeModal && (
        <FeeModal
          item={editingItem}
          onClose={() => setShowFeeModal(false)}
          onSuccess={() => { setShowFeeModal(false); fetchAllData(); showNotification('Platform fee saved', 'success'); }}
        />
      )}

      {showZoneModal && (
        <ZoneModal
          item={editingItem}
          onClose={() => setShowZoneModal(false)}
          onSuccess={() => { setShowZoneModal(false); fetchAllData(); showNotification('Pricing zone saved', 'success'); }}
        />
      )}

      {showTimeRuleModal && (
        <TimeRuleModal
          item={editingItem}
          onClose={() => setShowTimeRuleModal(false)}
          onSuccess={() => { setShowTimeRuleModal(false); fetchAllData(); showNotification('Time rule saved', 'success'); }}
        />
      )}
    </div>
  );
};

const VehiclePricingTab: React.FC<{
  vehicles: VehiclePricing[];
  onAdd: () => void;
  onEdit: (item: VehiclePricing) => void;
  onDelete: (id: number) => void;
  onRefresh: () => void;
}> = ({ vehicles, onAdd, onEdit, onDelete }) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Vehicle Pricing</h2>
        <button
          onClick={onAdd}
          className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Add Vehicle Type
        </button>
      </div>

      {vehicles.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <TruckIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No vehicle pricing configured yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {vehicles.map((vehicle) => (
            <div key={vehicle.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900 capitalize">{vehicle.vehicle_type}</h3>
                  <span className={`text-xs px-2 py-1 rounded ${vehicle.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {vehicle.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => onEdit(vehicle)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button onClick={() => onDelete(vehicle.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">Flat Fee:</span> <span className="font-medium">₦{(vehicle.flat_fee ?? 0).toLocaleString()}</span></div>
                <div><span className="text-gray-500">Per KM:</span> <span className="font-medium">₦{(vehicle.rate_per_km ?? 0).toLocaleString()}</span></div>
                <div><span className="text-gray-500">Min Fare:</span> <span className="font-medium">₦{(vehicle.minimum_fare ?? 0).toLocaleString()}</span></div>
                <div><span className="text-gray-500">Max Fare:</span> <span className="font-medium">₦{(vehicle.maximum_fare ?? 0).toLocaleString()}</span></div>
                <div><span className="text-gray-500">Max Weight:</span> <span className="font-medium">{vehicle.max_capacity_kg ?? 0}kg</span></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PlatformFeesTab: React.FC<{
  fees: PlatformFee[];
  onAdd: () => void;
  onEdit: (item: PlatformFee) => void;
  onDelete: (id: number) => void;
  onRefresh: () => void;
}> = ({ fees, onAdd, onEdit, onDelete }) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Platform Fees</h2>
        <button
          onClick={onAdd}
          className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Add Fee
        </button>
      </div>

      {fees.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No platform fees configured yet.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Fee Name</th>
                <th className="px-4 py-3 text-left font-semibold">Type</th>
                <th className="px-4 py-3 text-left font-semibold">Value</th>
                <th className="px-4 py-3 text-left font-semibold">Max Cap</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {fees.map((fee) => (
                <tr key={fee.id} className="border-b border-gray-100">
                  <td className="px-4 py-3 font-medium">{fee.fee_name || '-'}</td>
                  <td className="px-4 py-3 capitalize">{fee.fee_type || '-'}</td>
                  <td className="px-4 py-3">
                    {fee.fee_type === 'percentage' ? `${fee.fee_value ?? 0}%` : `₦${(fee.fee_value ?? 0).toLocaleString()}`}
                  </td>
                  <td className="px-4 py-3">
                    {fee.fee_cap != null ? `₦${fee.fee_cap.toLocaleString()}` : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded ${fee.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {fee.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => onEdit(fee)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button onClick={() => onDelete(fee.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const PricingZonesTab: React.FC<{
  zones: PricingZone[];
  onAdd: () => void;
  onEdit: (item: PricingZone) => void;
  onDelete: (id: number) => void;
  onRefresh: () => void;
}> = ({ zones, onAdd, onEdit, onDelete }) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Pricing Zones</h2>
        <button
          onClick={onAdd}
          className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Add Zone
        </button>
      </div>

      {zones.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No pricing zones configured yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {zones.map((zone) => (
            <div key={zone.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{zone.zone_name}</h3>
                  <span className="text-sm text-gray-500 capitalize">{zone.zone_type}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => onEdit(zone)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button onClick={() => onDelete(zone.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="mb-2">
                <span className="text-sm text-gray-500">Multiplier:</span>
                <span className="ml-2 font-medium text-lg">{zone.multiplier}x</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {(zone.areas || []).slice(0, 5).map((area, idx) => (
                  <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    {area}
                  </span>
                ))}
                {(zone.areas || []).length > 5 && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                    +{zone.areas.length - 5} more
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TimePricingTab: React.FC<{
  rules: TimePricingRule[];
  onAdd: () => void;
  onEdit: (item: TimePricingRule) => void;
  onDelete: (id: number) => void;
  onRefresh: () => void;
}> = ({ rules, onAdd, onEdit, onDelete }) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Time-Based Pricing Rules</h2>
        <button
          onClick={onAdd}
          className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Add Time Rule
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No time-based pricing rules configured yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rules.map((rule) => (
            <div key={rule.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{rule.rule_name}</h3>
                  <span className="text-sm text-gray-500 capitalize">{rule.rule_type}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => onEdit(rule)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button onClick={() => onDelete(rule.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                <div><span className="text-gray-500">Multiplier:</span> <span className="font-medium">{rule.multiplier}x</span></div>
                <div><span className="text-gray-500">Hours:</span> <span className="font-medium">{rule.start_hour}:00 - {rule.end_hour}:00</span></div>
              </div>
              <div className="flex gap-1">
                {(rule.day_of_week || []).map((day) => (
                  <span key={day} className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                    {dayNames[day]}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AIConfigTab: React.FC<{
  config: AIPricingConfig;
  setConfig: (config: AIPricingConfig) => void;
  onSave: () => void;
  saving: boolean;
}> = ({ config, setConfig, onSave, saving }) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">AI Pricing Configuration</h2>
          <p className="text-sm text-gray-500">Enable AI-powered dynamic pricing adjustments</p>
        </div>
        <button
          onClick={onSave}
          disabled={saving}
          className="px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <CpuChipIcon className="h-8 w-8 text-purple-600" />
            <div>
              <h3 className="font-semibold text-gray-900">AI-Powered Pricing</h3>
              <p className="text-sm text-gray-500">Automatically adjust prices based on market conditions</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.is_enabled}
              onChange={(e) => setConfig({ ...config, is_enabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
          </label>
        </div>

        {config.is_enabled && (
          <div className="mt-4 p-4 bg-purple-100 rounded-lg">
            <p className="text-sm text-purple-800">
              AI is analyzing market conditions and will adjust prices within your configured limits.
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Maximum Price Increase (%)
          </label>
          <input
            type="number"
            value={config.max_adjustment_percent}
            onChange={(e) => setConfig({ ...config, max_adjustment_percent: parseFloat(e.target.value) || 0 })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          />
          <p className="text-xs text-gray-500 mt-1">Maximum % the AI can increase prices</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Maximum Price Decrease (%)
          </label>
          <input
            type="number"
            value={Math.abs(config.min_adjustment_percent)}
            onChange={(e) => setConfig({ ...config, min_adjustment_percent: -Math.abs(parseFloat(e.target.value) || 0) })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          />
          <p className="text-xs text-gray-500 mt-1">Maximum % the AI can discount prices</p>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-4">AI Consideration Factors</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: 'consider_time_of_day', label: 'Time of Day', desc: 'Peak hours, late night' },
            { key: 'consider_demand', label: 'Demand Level', desc: 'Orders vs available dispatchers' },
            { key: 'consider_location_zone', label: 'Location Zones', desc: 'Premium vs standard areas' },
            { key: 'consider_cost_of_living', label: 'Cost of Living', desc: 'Regional cost differences' },
          ].map((factor) => (
            <label key={factor.key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <input
                type="checkbox"
                checked={(config as any)[factor.key]}
                onChange={(e) => setConfig({ ...config, [factor.key]: e.target.checked })}
                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
              />
              <div>
                <div className="font-medium text-gray-900">{factor.label}</div>
                <div className="text-xs text-gray-500">{factor.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

const VehicleModal: React.FC<{
  item: VehiclePricing | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ item, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    vehicle_type: item?.vehicle_type || '',
    flat_fee: item?.flat_fee || 300,
    rate_per_km: item?.rate_per_km || 100,
    rate_per_minute: item?.rate_per_minute || 10,
    minimum_fare: item?.minimum_fare || 500,
    maximum_fare: item?.maximum_fare || 50000,
    max_capacity_kg: item?.max_capacity_kg || 50,
    apply_weight_surcharge: item?.apply_weight_surcharge ?? true,
    apply_zone_multipliers: item?.apply_zone_multipliers ?? true,
    apply_time_pricing: item?.apply_time_pricing ?? true,
    is_active: item?.is_active ?? true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.vehicle_type.trim()) {
      setError('Vehicle type is required');
      return;
    }
    
    setSaving(true);
    try {
      if (item) {
        await adminAPI.updateVehiclePricing(item.id, formData);
      } else {
        await adminAPI.createVehiclePricing(formData);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{item ? 'Edit' : 'Add'} Vehicle Pricing</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Vehicle Type</label>
              <input
                type="text"
                value={formData.vehicle_type}
                onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value.toLowerCase() })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="e.g., motorcycle, car, van"
                disabled={!!item}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Flat Fee (₦)</label>
                <input
                  type="number"
                  value={formData.flat_fee}
                  onChange={(e) => setFormData({ ...formData, flat_fee: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Rate per KM (₦)</label>
                <input
                  type="number"
                  value={formData.rate_per_km}
                  onChange={(e) => setFormData({ ...formData, rate_per_km: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Rate per Minute (₦)</label>
                <input
                  type="number"
                  value={formData.rate_per_minute}
                  onChange={(e) => setFormData({ ...formData, rate_per_minute: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Min Fare (₦)</label>
                <input
                  type="number"
                  value={formData.minimum_fare}
                  onChange={(e) => setFormData({ ...formData, minimum_fare: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Max Fare (₦)</label>
                <input
                  type="number"
                  value={formData.maximum_fare}
                  onChange={(e) => setFormData({ ...formData, maximum_fare: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Max Weight (kg)</label>
                <input
                  type="number"
                  value={formData.max_capacity_kg}
                  onChange={(e) => setFormData({ ...formData, max_capacity_kg: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.apply_weight_surcharge}
                  onChange={(e) => setFormData({ ...formData, apply_weight_surcharge: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm">Apply weight surcharge</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.apply_zone_multipliers}
                  onChange={(e) => setFormData({ ...formData, apply_zone_multipliers: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm">Apply zone multipliers</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.apply_time_pricing}
                  onChange={(e) => setFormData({ ...formData, apply_time_pricing: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm">Apply time-based pricing</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm">Active</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const FeeModal: React.FC<{
  item: PlatformFee | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ item, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    fee_name: item?.fee_name || '',
    fee_type: item?.fee_type || 'percentage',
    fee_value: item?.fee_value || 0,
    fee_cap: item?.fee_cap || null as number | null,
    is_active: item?.is_active ?? true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fee_name.trim()) {
      setError('Fee name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (item) {
        await adminAPI.updatePlatformFee(item.id, formData);
      } else {
        await adminAPI.createPlatformFee(formData);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{item ? 'Edit' : 'Add'} Platform Fee</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Fee Name</label>
              <input
                type="text"
                value={formData.fee_name}
                onChange={(e) => setFormData({ ...formData, fee_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="e.g., Platform Fee, Insurance Fee"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Fee Type</label>
              <select
                value={formData.fee_type}
                onChange={(e) => setFormData({ ...formData, fee_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="percentage">Percentage</option>
                <option value="flat">Flat Amount</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Value {formData.fee_type === 'percentage' ? '(%)' : '(₦)'}
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.fee_value}
                onChange={(e) => setFormData({ ...formData, fee_value: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            {formData.fee_type === 'percentage' && (
              <div>
                <label className="block text-sm font-medium mb-1">Max Cap (₦) - Optional</label>
                <input
                  type="number"
                  value={formData.fee_cap || ''}
                  onChange={(e) => setFormData({ ...formData, fee_cap: e.target.value ? parseFloat(e.target.value) : null })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Leave empty for no cap"
                />
              </div>
            )}

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm">Active</span>
            </label>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const ZoneModal: React.FC<{
  item: PricingZone | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ item, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    zone_name: item?.zone_name || '',
    zone_type: item?.zone_type || 'premium',
    multiplier: item?.multiplier || 1.0,
    areas: item?.areas || [] as string[],
    is_active: item?.is_active ?? true
  });
  const [areasInput, setAreasInput] = useState((item?.areas || []).join(', '));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.zone_name.trim()) {
      setError('Zone name is required');
      return;
    }
    
    const areas = areasInput.split(',').map(a => a.trim()).filter(a => a);
    
    setSaving(true);
    try {
      const payload = { ...formData, areas };
      if (item) {
        await adminAPI.updatePricingZone(item.id, payload);
      } else {
        await adminAPI.createPricingZone(payload);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{item ? 'Edit' : 'Add'} Pricing Zone</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Zone Name</label>
              <input
                type="text"
                value={formData.zone_name}
                onChange={(e) => setFormData({ ...formData, zone_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="e.g., Victoria Island Premium"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Zone Type</label>
              <select
                value={formData.zone_type}
                onChange={(e) => setFormData({ ...formData, zone_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="premium">Premium</option>
                <option value="standard">Standard</option>
                <option value="suburban">Suburban</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Multiplier</label>
              <input
                type="number"
                step="0.01"
                value={formData.multiplier}
                onChange={(e) => setFormData({ ...formData, multiplier: parseFloat(e.target.value) || 1 })}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">1.0 = no change, 1.2 = 20% increase</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Areas (comma-separated)</label>
              <textarea
                value={areasInput}
                onChange={(e) => setAreasInput(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
                placeholder="Victoria Island, Lekki, Ikoyi"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm">Active</span>
            </label>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const TimeRuleModal: React.FC<{
  item: TimePricingRule | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ item, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    rule_name: item?.rule_name || '',
    rule_type: item?.rule_type || 'peak_hour',
    multiplier: item?.multiplier || 1.0,
    day_of_week: item?.day_of_week || [0, 1, 2, 3, 4] as number[],
    start_hour: item?.start_hour || 7,
    end_hour: item?.end_hour || 9,
    is_active: item?.is_active ?? true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleDay = (day: number) => {
    if (formData.day_of_week.includes(day)) {
      setFormData({ ...formData, day_of_week: formData.day_of_week.filter(d => d !== day) });
    } else {
      setFormData({ ...formData, day_of_week: [...formData.day_of_week, day].sort() });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.rule_name.trim()) {
      setError('Rule name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (item) {
        await adminAPI.updateTimePricingRule(item.id, formData);
      } else {
        await adminAPI.createTimePricingRule(formData);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{item ? 'Edit' : 'Add'} Time Pricing Rule</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Rule Name</label>
              <input
                type="text"
                value={formData.rule_name}
                onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="e.g., Morning Peak Hours"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Rule Type</label>
              <select
                value={formData.rule_type}
                onChange={(e) => setFormData({ ...formData, rule_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="peak_hour">Peak Hour</option>
                <option value="off_peak">Off Peak</option>
                <option value="weekend">Weekend</option>
                <option value="holiday">Holiday</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Multiplier</label>
              <input
                type="number"
                step="0.01"
                value={formData.multiplier}
                onChange={(e) => setFormData({ ...formData, multiplier: parseFloat(e.target.value) || 1 })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Start Hour</label>
                <select
                  value={formData.start_hour}
                  onChange={(e) => setFormData({ ...formData, start_hour: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">End Hour</label>
                <select
                  value={formData.end_hour}
                  onChange={(e) => setFormData({ ...formData, end_hour: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Days of Week</label>
              <div className="flex flex-wrap gap-2">
                {dayNames.map((day, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => toggleDay(idx)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      formData.day_of_week.includes(idx)
                        ? 'bg-sky-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm">Active</span>
            </label>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PricingManagement;
